<template>
  <div class="max-w-6xl mx-auto">
    <div class="bg-background-light rounded-lg shadow-sm">
      <div class="p-6 border-b border-background-lighter">
        <div>
          <h2 class="text-2xl font-bold text-text-primary">Charge Wallet</h2>
          <p class="text-text-secondary mt-1">Add funds to your account wallet</p>
        </div>
      </div>

      <div class="p-6">
        <!-- Currency Selection -->
        <div class="mb-8">
          <h3 class="text-lg font-semibold text-text-primary mb-4">Select Currency</h3>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div
                v-for="currency in currencies"
                :key="currency.code"
                @click="selectCurrency(currency.code)"
                :class="[
                  'p-4 border-2 rounded-lg cursor-pointer transition-all text-center',
                  selectedCurrency === currency.code
                    ? 'border-primary bg-primary/10'
                    : 'border-background-lighter hover:border-primary/50'
                ]"
            >
              <div class="text-2xl mb-1">{{ currency.icon }}</div>
              <div class="font-semibold text-text-primary text-sm">{{ currency.name }}</div>
              <div class="text-xs text-text-secondary">{{ currency.code }}</div>
            </div>
          </div>
        </div>

        <!-- Payment Methods -->
        <div v-if="selectedCurrency" class="mb-8">
          <h3 class="text-lg font-semibold text-text-primary mb-4">
            Available Payment Methods for {{ selectedCurrency }}
          </h3>

          <!-- Loading Payment Methods -->
          <div v-if="isLoadingMethods" class="flex justify-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>

          <!-- Payment Methods List -->
          <div v-else-if="filteredPaymentMethods.length > 0" class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
            <div
                v-for="method in filteredPaymentMethods"
                :key="method.id"
                @click="selectPaymentMethod(method)"
                :class="[
                  'border-2 rounded-lg p-3 cursor-pointer transition-all',
                  selectedPaymentMethod?.id === method.id
                    ? 'border-primary bg-primary/10'
                    : 'border-background-lighter hover:border-primary/50'
                ]"
            >
              <div class="flex items-start space-x-3">
                <img :src="method.icon" :alt="method.name" class="w-10 h-10 object-contain flex-shrink-0" />
                <div class="flex-1 min-w-0">
                  <h4 class="font-semibold text-text-primary text-sm">{{ method.name }}</h4>
<!--                  <p class="text-xs text-text-secondary mt-1 line-clamp-2">{{ method.description }}</p>-->
                  <div class="flex items-center mt-2 space-x-1 flex-wrap">
                    <span v-if="method.requires_proof" class="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      Proof Required
                    </span>
                    <span v-if="method.is_charge" class="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                      Charge Enabled
                    </span>

                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Payment Methods -->
          <div v-else-if="!isLoadingMethods" class="text-center py-8">
            <p class="text-text-secondary">No payment methods available for {{ selectedCurrency }}</p>
          </div>
        </div>

        <!-- Payment Form -->
        <div v-if="selectedPaymentMethod" class="space-y-6">
          <!-- Payment Instructions -->
          <div>
            <h3 class="text-lg font-semibold text-text-primary mb-3">Payment Instructions</h3>
            <div class="bg-background p-4 rounded-lg border border-background-lighter">
              <pre class="text-sm text-text-secondary whitespace-pre-wrap">{{ selectedPaymentMethod.instructions }}</pre>
            </div>
          </div>

          <!-- Amount Input -->
          <div>
            <label class="block text-sm font-medium text-text-primary mb-2">
              Amount ({{ selectedCurrency }}) *
            </label>
            <input
                v-model="chargeAmount"
                type="number"
                step="0.01"
                min="1"
                placeholder="Enter amount"
                class="w-full px-4 py-3 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
            />
            <p v-if="chargeAmount && chargeAmount < 1" class="text-red-600 text-sm mt-1">
              Minimum amount is 1 {{ selectedCurrency }}
            </p>
          </div>

          <!-- Proof Upload -->
          <div v-if="selectedPaymentMethod.requires_proof">
            <label class="block text-sm font-medium text-text-primary mb-2">
              Upload Payment Proof *
            </label>
            <input
                ref="fileInput"
                type="file"
                accept="image/*,.pdf"
                @change="handleFileUpload"
                class="w-full px-4 py-3 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
            />
            <p class="text-sm text-text-secondary mt-1">
              Upload screenshot or receipt of your payment (JPG, PNG, PDF - Max 5MB)
            </p>
            <p v-if="uploadedFile" class="text-green-600 text-sm mt-1">
              ✓ File selected: {{ uploadedFile.name }}
            </p>
          </div>

          <!-- Additional Notes -->
          <div>
            <label class="block text-sm font-medium text-text-primary mb-2">
              Additional Notes (Optional)
            </label>
            <textarea
                v-model="additionalNotes"
                rows="3"
                placeholder="Any additional information about your payment..."
                class="w-full px-4 py-3 border border-background-lighter rounded-md bg-background focus:outline-none focus:border-primary"
            ></textarea>
          </div>

          <!-- Summary -->
          <div class="bg-background-lighter p-6 rounded-lg">
            <h3 class="text-lg font-semibold text-text-primary mb-4">Request Summary</h3>
            <div class="space-y-3">
              <div class="flex justify-between">
                <span class="text-text-secondary">Currency:</span>
                <span class="font-semibold">{{ selectedCurrency }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-text-secondary">Amount:</span>
                <span class="font-semibold">{{ chargeAmount || '0.00' }} {{ selectedCurrency }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-text-secondary">Payment Method:</span>
                <span class="font-semibold">{{ selectedPaymentMethod.name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-text-secondary">Status:</span>
                <span class="text-yellow-600 font-semibold">Pending Approval</span>
              </div>
            </div>
          </div>

          <!-- Submit Button -->
          <div class="flex space-x-4">
            <button
                @click="submitRequest"
                :disabled="!canSubmit || isSubmitting"
                :class="[
                  'flex-1 py-3 px-6 rounded-md font-semibold transition-colors',
                  canSubmit && !isSubmitting
                    ? 'bg-primary text-white hover:bg-primary-dark'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
            >
              <span v-if="isSubmitting">Submitting...</span>
              <span v-else>Submit Request</span>
            </button>

            <button
                @click="resetForm"
                class="px-6 py-3 border border-background-lighter rounded-md text-text-secondary hover:bg-background-lighter transition-colors"
            >
              Reset
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { getPaymentMethods, createChargeRequest } from '@/services/api.js';
import Swal from 'sweetalert2';

export default {
  name: 'ChargeWallet',
  setup() {
    // Form data
    const selectedCurrency = ref('');
    const selectedPaymentMethod = ref(null);
    const chargeAmount = ref('');
    const additionalNotes = ref('');
    const uploadedFile = ref(null);

    // Loading states
    const isLoadingMethods = ref(false);
    const isSubmitting = ref(false);

    // Data
    const allPaymentMethods = ref([]);

    // Available currencies
    const currencies = ref([
      {
        code: 'DZD',
        name: 'Algerian Dinar',
        icon: '🇩🇿'
      },
      {
        code: 'USD',
        name: 'US Dollar',
        icon: '🇺🇸'
      },
      {
        code: 'EUR',
        name: 'Euro',
        icon: '🇪🇺'
      },
      {
        code: 'EGP',
        name: 'Egyptian Pound',
        icon: '🇪🇬'
      }
    ]);

    // Computed properties
    const filteredPaymentMethods = computed(() => {
      if (!selectedCurrency.value) return [];
      return allPaymentMethods.value.filter(method =>
          method.supported_currencies.includes(selectedCurrency.value) &&
          method.is_bulk &&
          method.is_charge
      );
    });

    const canSubmit = computed(() => {
      return selectedCurrency.value &&
          selectedPaymentMethod.value &&
          chargeAmount.value >= 1 &&
          (!selectedPaymentMethod.value.requires_proof || uploadedFile.value);
    });

    // Methods
    const selectCurrency = (currency) => {
      selectedCurrency.value = currency;
      selectedPaymentMethod.value = null;
      chargeAmount.value = '';
      additionalNotes.value = '';
      uploadedFile.value = null;
      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    };

    const selectPaymentMethod = (method) => {
      selectedPaymentMethod.value = method;
    };

    const fetchAllPaymentMethods = async () => {
      isLoadingMethods.value = true;
      try {
        const response = await getPaymentMethods();
        allPaymentMethods.value = response.data || response;
      } catch (error) {
        console.error('Failed to fetch payment methods:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load payment methods. Please try again.',
          icon: 'error'
        });
      } finally {
        isLoadingMethods.value = false;
      }
    };

    const handleFileUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          Swal.fire({
            title: 'File Too Large',
            text: 'Please upload a file smaller than 5MB.',
            icon: 'warning'
          });
          event.target.value = '';
          uploadedFile.value = null;
          return;
        }

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
          Swal.fire({
            title: 'Invalid File Type',
            text: 'Please upload a JPG, PNG, or PDF file.',
            icon: 'warning'
          });
          event.target.value = '';
          uploadedFile.value = null;
          return;
        }

        uploadedFile.value = file;
      }
    };

    const submitRequest = async () => {
      if (!canSubmit.value) return;

      isSubmitting.value = true;

      try {
        const formData = new FormData();
        formData.append('wallet_currency', selectedCurrency.value);
        formData.append('amount', chargeAmount.value);
        formData.append('payment_method_id', selectedPaymentMethod.value.id);
        formData.append('notes', additionalNotes.value || 'No comment');

        if (uploadedFile.value) {
          formData.append('proof_file', uploadedFile.value);
        }

        const response = await createChargeRequest(formData);

        Swal.fire({
          title: 'Success!',
          text: 'Your charge request has been submitted successfully. It will be reviewed by our team.',
          icon: 'success',
          timer: 3000,
          showConfirmButton: false
        });

        resetForm();

      } catch (error) {
        console.error('Failed to submit charge request:', error);
        Swal.fire({
          title: 'Error!',
          text: error.response?.data?.message || 'Failed to submit charge request. Please try again.',
          icon: 'error'
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    const resetForm = () => {
      selectedCurrency.value = '';
      selectedPaymentMethod.value = null;
      chargeAmount.value = '';
      additionalNotes.value = '';
      uploadedFile.value = null;

      // Clear file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
    };

    // Load all payment methods on mount
    onMounted(() => {
      fetchAllPaymentMethods();
    });

    return {
      selectedCurrency,
      selectedPaymentMethod,
      chargeAmount,
      additionalNotes,
      uploadedFile,
      isLoadingMethods,
      isSubmitting,
      currencies,
      filteredPaymentMethods,
      canSubmit,
      selectCurrency,
      selectPaymentMethod,
      handleFileUpload,
      submitRequest,
      resetForm
    };
  }
}
</script>

<style scoped>
pre {
  font-family: inherit;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
